<template>
  <div class="upload-container" :class="isPc ? 'pc': 'mobile'">
    <div :class="[isPc ? 'body-container pc': 'body-container mobile', isDisabled ? 'disabled': '']">
      <el-upload
        class="upload"
        drag
        list-type="'text"
        :multiple="false"
        :action="qiniuUploadUrl"
        :data="qiniuData"
        :before-upload="handleBeforeUpload"
        :file-list="fileList"
        :on-success="handleUploadSuccess"
        :on-error="handleUploadError"
        :on-remove="handleFileRemove"
        :disabled="isDisabled"
        >
        <i class="el-icon-upload"></i>
        <div class="el-upload__text">
          <em>{{ $t('microSite.form.label.clickUpload') }}</em>
          <span class="tips">{{ $t('microSite.form.label.uploadTips', { maxSize: 5 }) }}</span>
        </div>
      </el-upload>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapGetters } from 'vuex'

export default {
  props: {
    value: {
    },
    title: {
      type: String,
      default: ''
    },
    isPc: {
      type: Boolean,
      default: true
    },
    isRequired: {
      type: Boolean,
      default: true
    },
    isDisabled: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      acceptFileSuffix: ['pdf', 'doc', 'docx', 'html', 'png', 'jpeg', 'jpg', 'bmp', 'msword', 'document'],
      internalValue: {
        fileName: '',
        fileUrl: '',
        fileKey: ''
      },
      qiniuUploadUrl: window.location.protocol + '//upload.qiniup.com',
      qiniuData: {
        key: null,
        token: null
      },
      hasFileUpload: false,
      limit: 1,
      isMultiple: false,
      fileList: [],
      maxSize: 5,
      uploadPrefix: 'microsite/form/submit/'
    }
  },
  computed: {
  },
  async created () {
    if (this.isDisabled) {
      return
    }
    this.internalValue = _.isNil(this.value) || this.value === '' ? { fileName: '', fileUrl: '', fileKey: '' } : this.value
    this.isMultiple = this.internalValue instanceof Array
    if (this.isMultiple) {
      this.limit = null
    }
    this.getFileList(this.internalValue)
    const resp = await this.getQiniuUploadToken({ is_public: false })
    this.qiniuData.token = resp.token
  },
  mounted () {
  },
  methods: {
    ...mapActions('common', ['getQiniuUploadToken']),
    ...mapGetters('account', ['getCurrentAccount']),
    handleBeforeUpload (file) {
      if (!this.isAcceptFile(file.type)) {
        this.$message.error(this.$t('microSite.component.upload.fileTypeError'))
        return false
      }
      if (file.size > this.maxSize * 1024 * 1024) {
        this.$message.error(this.$t('microSite.component.upload.fileSizeError', { maxSize: this.maxSize }))
        return false
      }
      this.qiniuData.key = this.fileKeyGenerator(file)
      return true
    },
    handleUploadSuccess (resp, file, fileList) {
      this.hasFileUpload = true
      const url = `${process.env.VUE_APP_QINIU_DOMAIN}${this.qiniuData.key}`
      if (this.isMultiple) {
        if (!this.internalValue.fileUrl) {
          this.internalValue = []
        }
        this.internalValue.push({
          fileName: file.name,
          fileUrl: url,
          fileKey: this.qiniuData.key
        })
      } else {
        this.internalValue = {
          fileName: file.name,
          fileUrl: url,
          fileKey: this.qiniuData.key
        }
      }
    },
    handleUploadError (resp, file, fileList) {
      this.hasFileUpload = false
      if (!this.isMultiple) {
        this.internalValue = {
          fileName: '',
          fileUrl: '',
          fileKey: ''
        }
      }
      this.$message({
        message: this.$t('microSite.component.upload.uploadFailed'),
        duration: 2000,
        type: 'warning'
      })
    },
    handleFileRemove (file, fileList) {
      this.hasFileUpload = false
      if (this.isMultiple) {
        if (this.internalValue) {
          const fileIndex = this.internalValue.indexOf(file)
          if (fileIndex > -1) {
            this.internalValue.splice(fileIndex, 1)
          }
        }
      } else {
        this.internalValue = {
          fileName: '',
          fileUrl: '',
          fileKey: ''
        }
      }
    },
    fileKeyGenerator (file) {
      return this.uploadPrefix + this.getCurrentAccount().id + '/' + new Date().valueOf()
    },
    getFileList (file) {
      const tempFileList = []
      if (!file) {
        return
      }
      if (file instanceof Array) {
        for (const item of file) {
          tempFileList.push({ name: item.fileName, url: '' })
        }
      } else {
        if (file.fileName) {
          tempFileList.push({ name: file.fileName, url: '' })
        }
      }
      this.fileList = tempFileList
      this.hasFileUpload = this.fileList.length > 0
    },
    isAcceptFile (fileType) {
      return _.find(this.acceptFileSuffix, (item) => fileType.endsWith(item))
    }
  },
  watch: {
    value: {
      handler (val, oldVal) {
        this.internalValue = val
        this.getFileList(this.internalValue)
      },
      immediate: true
    },
    internalValue (val) {
      this.$emit('input', val)
    }
  }
}
</script>

<style lang="scss" scoped>
.upload-container {
  padding: 10px 0 0 0;

  &.pc {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  > .disabled {
    ::v-deep .upload {
      .el-upload {
        cursor: not-allowed;
      }
      .el-upload-dragger {
        cursor: not-allowed;
      }
    }
  }

  .body-container {

    .el-upload__text {
      display: flex;
      justify-content: center;
      flex-direction: column;
    }

    ::v-deep .upload {
      .el-upload {
        width: 100%;
      }

      .el-upload-dragger {
        width: unset;
        height: unset;
      }
    }

    &.mobile {
      .el-icon-upload {
        margin: 20px 0 16px;
      }
    }

    &.pc {
      width: 35%;
    }
  }
}
</style>
